import Link from "@whoppah/next/link";

import { cn } from "@/common/utils/css.utils";
import { HelpCategoryView } from "@/common/utils/help/help-category.utils";
import {
  getHelpArticlePath,
  getHelpCategoryPath,
} from "@/common/utils/help/help-url.utils";
import { HelpArticleListItem } from "@/features/help/api";
import { Body } from "@/ui/typography/Body";

type HelpSidebarCategoryProps = {
  categories: HelpCategoryView[];
  currentSlug: string;
};

type HelpSidebarArticlesProps = {
  articles: HelpArticleListItem[];
  categorySlug: string;
  currentSlug: string;
};

type HelpSidebarProps = HelpSidebarCategoryProps | HelpSidebarArticlesProps;

const CategoryNode = ({
  category,
  currentSlug,
}: {
  category: HelpCategoryView;
  currentSlug: string;
}) => {
  const isCurrent = category.slug === currentSlug;

  return (
    <li>
      <Link
        href={getHelpCategoryPath(category.slug)}
        className={cn(
          "block border-l-2 py-1.5 pl-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-curious-200",
          isCurrent
            ? "border-brand-500 font-bold text-brand-500"
            : "border-transparent text-black hover:underline"
        )}
      >
        <Body as="span" className={cn(isCurrent && "font-bold text-brand-500")}>
          {category.title}
        </Body>
        <Body as="span" className="ml-2 text-grey-500">
          {category.articleCount}
        </Body>
      </Link>
      {category.children.length ? (
        <ul className="ml-2">
          {category.children.map(child => (
            <CategoryNode
              key={child.id}
              category={child}
              currentSlug={currentSlug}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

const HelpSidebarCategories = ({
  categories,
  currentSlug,
}: HelpSidebarCategoryProps) => (
  <nav aria-label="Help categories">
    <ul>
      {categories.map(category => (
        <CategoryNode
          key={category.id}
          category={category}
          currentSlug={currentSlug}
        />
      ))}
    </ul>
  </nav>
);

const HelpSidebarArticles = ({
  articles,
  categorySlug,
  currentSlug,
}: HelpSidebarArticlesProps) => (
  <nav aria-label="Help articles">
    <ul>
      {articles.map(article => {
        const isCurrent = article.slug === currentSlug;

        return (
          <li key={article.id}>
            <Link
              href={getHelpArticlePath(
                article.categorySlug || categorySlug,
                article.slug
              )}
              className={cn(
                "block border-l-2 py-1.5 pl-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-curious-200",
                isCurrent
                  ? "border-brand-500 font-bold text-brand-500"
                  : "border-transparent text-black hover:underline"
              )}
            >
              <Body
                as="span"
                className={cn(isCurrent && "font-bold text-brand-500")}
              >
                {article.title}
              </Body>
            </Link>
          </li>
        );
      })}
    </ul>
  </nav>
);

export const HelpSidebar = (props: HelpSidebarProps) => {
  return (
    <aside className="hidden w-[280px] shrink-0 lg:block">
      <div className="sticky top-24">
        {"categories" in props ? (
          <HelpSidebarCategories
            categories={props.categories}
            currentSlug={props.currentSlug}
          />
        ) : (
          <HelpSidebarArticles
            articles={props.articles}
            categorySlug={props.categorySlug}
            currentSlug={props.currentSlug}
          />
        )}
      </div>
    </aside>
  );
};
